import React, { useState, useEffect, useRef } from "react";

import { useNavigate, useLocation } from "react-router-dom";

import { motion, AnimatePresence } from "framer-motion";

import { GiHamburgerMenu } from "react-icons/gi";
import { IoIosArrowDown, IoMdClose } from "react-icons/io";

import {
  ArrowUpRight,
  ChevronRight,
  Code2,
  Globe,
  Smartphone,
  Palette,
  Cloud,
  BrainCircuit,
  MessageSquare,
  Scale,
  Package,
  Building2,
  FileCheck,
  Receipt,
  BadgeCheck,
  ShieldCheck,
  Moon,
  Sun,
} from "lucide-react";

import WBLogo from "../../Assets/Images/WB.png";
import WBBlackLogo from "../../Assets/Images/WBLogo.png";

import ContactModal from "../../Assets/Common/ContactModal";

import { useTheme } from "../../context/ThemeContext";

/* ========================================================================= */
/* SERVICES DATA */
/* ========================================================================= */

const servicesData = {
  software: {
    title: "Software Development",
    mainPath: "/services/software-development",

    services: [
      {
        title: "Custom Software Development",
        icon: Code2,
        slug: "custom-software",
        path: "/services/software-development/custom-software",
      },
      {
        title: "Mobile App Development",
        icon: Smartphone,
        slug: "mobile-app-development",
        path: "/services/software-development/mobile-app-development",
      },
      {
        title: "Web Development",
        icon: Globe,
        slug: "web-development",
        path: "/services/software-development/web-development",
      },
      {
        title: "UI / UX Design",
        icon: Palette,
        slug: "ui-ux-design",
        path: "/services/software-development/ui-ux-design",
      },
      {
        title: "CRM Solutions",
        icon: Cloud,
        slug: "crm-solutions",
        path: "/services/software-development/crm-solutions",
      },
      {
        title: "MLM Software",
        icon: BrainCircuit,
        slug: "mlm-software",
        path: "/services/software-development/mlm-software",
      },
    ],
  },

  api: {
    title: "API Services",
    mainPath: "/services/api",

    services: [
      {
        title: "Whatsapp API Services",
        icon: MessageSquare,
        slug: "whatsapp-api",
        path: "/services/api/whatsapp-api",
      },
      {
        title: "SMS OTP Services",
        icon: ShieldCheck,
        slug: "sms-otp",
        path: "/services/api/sms-otp",
      },
    ],
  },

  legal: {
    title: "Legal Services",
    mainPath: "/services/legal-services",

    services: [
      {
        title: "Business Registration",
        icon: Building2,
        slug: "business-registration",
        path: "/services/legal-services/business-registration",
      },
      {
        title: "Company Registration",
        icon: FileCheck,
        slug: "company-registration",
        path: "/services/legal-services/company-registration",
      },
      {
        title: "Taxation Services",
        icon: Receipt,
        slug: "taxation-services",
        path: "/services/legal-services/taxation-services",
      },
      {
        title: "ISO Certification",
        icon: BadgeCheck,
        slug: "iso-certification",
        path: "/services/legal-services/iso-certification",
      },
      {
        title: "IPR Services",
        icon: ShieldCheck,
        slug: "ipr-services",
        path: "/services/legal-services/ipr-services",
      },
    ],
  },
};

/* ========================================================================= */
/* LEFT PANEL CATEGORIES */
/* ========================================================================= */

const serviceCategories = [
  {
    id: "software",
    title: "Software Development",
    icon: Code2,
    hasChildren: true,
  },
  {
    id: "api",
    title: "API Services",
    icon: MessageSquare,
    hasChildren: true,
  },
  {
    id: "legal",
    title: "Legal Services",
    icon: Scale,
    hasChildren: true,
  },
  {
    id: "product",
    title: "Product Development",
    icon: Package,
    comingSoon: true,
  },
];

/* ========================================================================= */
/* NAVIGATION ITEMS */
/* ========================================================================= */

const menuItems = [
  {
    title: "Home",
    path: "/",
  },
  {
    title: "Services",
    megaMenu: true,
  },
  {
    title: "Portfolio",
    path: "/portfolio",
  },
  {
    title: "Blogs",
    path: "/blogs",
  },
  {
    title: "Career",
    path: "/career",
  },
  {
    title: "Contact",
    path: "/contact",
  },
  {
    title: "About Us",
    path: "/about",
  },
];

/* ========================================================================= */
/* COMPONENT */
/* ========================================================================= */

const BottomNavbar = () => {
  const navigate = useNavigate();
  const location = useLocation();

  /* ----------------------------------------------------------------------- */
  /* THEME                                                                   */
  /* ----------------------------------------------------------------------- */

  const { theme, toggleTheme } = useTheme();

  const isDark = theme === "dark";

  const currentLogo = isDark ? WBLogo : WBBlackLogo;

  /* ----------------------------------------------------------------------- */
  /* REFS                                                                    */
  /* ----------------------------------------------------------------------- */

  const desktopMenuRef = useRef(null);
  const mobileDrawerRef = useRef(null);

  /* ----------------------------------------------------------------------- */
  /* STATES                                                                  */
  /* ----------------------------------------------------------------------- */

  const [mobileOpen, setMobileOpen] = useState(false);

  const [servicesOpen, setServicesOpen] = useState(false);
  const [activeCategory, setActiveCategory] = useState("");

  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const [mobileExpandedCategory, setMobileExpandedCategory] =
    useState("");

  const [openModal, setOpenModal] = useState(false);

  /* ----------------------------------------------------------------------- */
  /* ACTIVE SERVICES                                                         */
  /* ----------------------------------------------------------------------- */

  const activeServices =
    servicesData[activeCategory]?.services || [];

  /* ========================================================================= */
  /* CLOSE MENUS ON OUTSIDE CLICK                                             */
  /* ========================================================================= */

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        desktopMenuRef.current &&
        !desktopMenuRef.current.contains(event.target)
      ) {
        setServicesOpen(false);
        setActiveCategory("");
      }

      if (
        mobileDrawerRef.current &&
        !mobileDrawerRef.current.contains(event.target)
      ) {
        setMobileOpen(false);
        setMobileServicesOpen(false);
        setMobileExpandedCategory("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener(
        "mousedown",
        handleClickOutside
      );
    };
  }, []);

  /* ========================================================================= */
  /* NAVIGATION                                                               */
  /* ========================================================================= */

  const handleNavigate = (path) => {
    if (!path) return;

    navigate(path);

    setServicesOpen(false);
    setActiveCategory("");

    setMobileServicesOpen(false);
    setMobileExpandedCategory("");
    setMobileOpen(false);

    window.setTimeout(() => {
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }, 50);
  };

  /* ========================================================================= */
  /* MAIN SERVICE NAVIGATION                                                  */
  /* ========================================================================= */

  const handleMainServiceNavigation = (categoryId) => {
    const category = servicesData[categoryId];

    if (!category?.mainPath) return;

    handleNavigate(category.mainPath);
  };

  /* ========================================================================= */
  /* DESKTOP CATEGORY HOVER                                                    */
  /* ========================================================================= */

  const handleCategoryHover = (category) => {
    if (category.hasChildren && !category.comingSoon) {
      setActiveCategory(category.id);
    } else {
      setActiveCategory("");
    }
  };

  /* ========================================================================= */
  /* MOBILE SERVICES TOGGLE                                                   */
  /* ========================================================================= */

  const toggleMobileServices = () => {
    setMobileServicesOpen((previous) => {
      const next = !previous;

      if (!next) {
        setMobileExpandedCategory("");
      }

      return next;
    });
  };

  /* ========================================================================= */
  /* MOBILE CATEGORY EXPANSION                                                 */
  /* ========================================================================= */

  const toggleMobileCategory = (id) => {
    setMobileExpandedCategory((previous) =>
      previous === id ? "" : id
    );
  };

  /* ========================================================================= */
  /* CLOSE MOBILE DRAWER                                                       */
  /* ========================================================================= */

  const closeMobileDrawer = () => {
    setMobileOpen(false);
    setMobileServicesOpen(false);
    setMobileExpandedCategory("");
  };

  /* ========================================================================= */
  /* THEME TOGGLE BUTTON                                                       */
  /* ========================================================================= */

  const ThemeToggleButton = () => {
    return (
      <button
        type="button"
        onClick={toggleTheme}
        aria-label={
          isDark
            ? "Switch to light mode"
            : "Switch to dark mode"
        }
        title={
          isDark
            ? "Switch to light mode"
            : "Switch to dark mode"
        }
        className="
          group
          flex
          h-10
          w-10
          shrink-0
          items-center
          justify-center
          rounded-xl
          border
          theme-border
          theme-card
          theme-text-secondary
          transition-all
          duration-300
          hover:border-primaryColor/40
          hover:bg-primaryColor/10
          hover:text-primaryColor

          sm:h-11
          sm:w-11
        "
      >
        <motion.div
          key={isDark ? "moon" : "sun"}
          initial={{
            rotate: -90,
            scale: 0.6,
            opacity: 0,
          }}
          animate={{
            rotate: 0,
            scale: 1,
            opacity: 1,
          }}
          transition={{
            duration: 0.25,
          }}
        >
          {isDark ? (
            <Moon
              size={18}
              strokeWidth={2}
            />
          ) : (
            <Sun
              size={18}
              strokeWidth={2}
            />
          )}
        </motion.div>
      </button>
    );
  };

  return (
    <>
      {/* =================================================================== */}
      {/* NAVBAR WRAPPER                                                      */}
      {/* =================================================================== */}

      <div className="fixed inset-x-0 top-4 z-50 px-3 sm:top-5 sm:px-4 md:px-5">

        {/* ================================================================= */}
        {/* DESKTOP NAVBAR                                                    */}
        {/* ================================================================= */}

        <div
          className="
            webbocket-theme-navbar
            hidden
            xl:flex
            items-center
            justify-between
            max-w-7xl
            mx-auto
            rounded-2xl
            border
            theme-border
            theme-card
            px-8
            py-3
            shadow-[0_12px_40px_var(--shadow-color)]
            backdrop-blur-xl
            transition-all
            duration-300
          "
        >
          {/* DESKTOP LOGO */}

          <div
            onClick={() => handleNavigate("/")}
            className="
              flex
              cursor-pointer
              items-center
            "
          >
            <img
              src={currentLogo}
              alt="WebBocket"
              className="
                h-14
                w-40
                origin-left
                object-left
                scale-[3]
                object-contain
              "
            />
          </div>

          {/* DESKTOP MENU */}

          <div
            ref={desktopMenuRef}
            className="flex items-center gap-10"
          >
            {menuItems.map((item) => (
              <div
                key={item.title}
                className="relative"
              >
                {item.megaMenu ? (
                  <>
                    <button
                      onMouseEnter={() => {
                        setServicesOpen(true);
                        setActiveCategory("");
                      }}
                      className={`
                        flex
                        items-center
                        gap-2
                        font-medium
                        transition-all
                        duration-300

                        ${
                          servicesOpen
                            ? "text-primaryColor"
                            : "theme-text-secondary hover:text-primaryColor"
                        }
                      `}
                    >
                      Services

                      <motion.div
                        animate={{
                          rotate: servicesOpen ? 180 : 0,
                        }}
                      >
                        <IoIosArrowDown />
                      </motion.div>
                    </button>

                    <AnimatePresence>
                      {servicesOpen && (
                        <>
                          {/* OVERLAY */}

                          <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="
                              fixed
                              inset-0
                              z-40
                            "
                            style={{
                              top: "80px",
                              backdropFilter: "blur(8px)",
                              WebkitBackdropFilter: "blur(8px)",
                              background: isDark
                                ? "rgba(0,0,0,.12)"
                                : "rgba(255,255,255,.20)",
                            }}
                          />

                          {/* MEGA MENU */}

                          <motion.div
                            initial={{
                              opacity: 0,
                              y: -8,
                              scale: 0.98,
                            }}
                            animate={{
                              opacity: 1,
                              y: 0,
                              scale: 1,
                            }}
                            exit={{
                              opacity: 0,
                              y: -8,
                              scale: 0.98,
                            }}
                            transition={{
                              duration: 0.18,
                            }}
                            onMouseLeave={() => {
                              setServicesOpen(false);
                              setActiveCategory("");
                            }}
                            className="
                              absolute
                              left-1/2
                              top-full
                              z-50
                              mt-4
                              -translate-x-1/2
                            "
                          >
                            <div className="flex items-start">

                              {/* LEFT PANEL */}

                              <div
                                className="
                                  w-[365px]
                                  overflow-hidden
                                  rounded-3xl
                                  border
                                  theme-border
                                  theme-card
                                  backdrop-blur-2xl
                                  shadow-[0_20px_60px_var(--shadow-color)]
                                "
                              >
                                {/* HEADER */}

                                <div
                                  className="
                                    border-b
                                    theme-border
                                    bg-gradient-to-r
                                    from-primaryColor/10
                                    via-primaryColor/5
                                    to-transparent
                                    px-5
                                    py-4
                                  "
                                >
                                  <div className="flex items-center gap-3">
                                    <span
                                      className="
                                        h-2
                                        w-2
                                        rounded-full
                                        bg-primaryColor
                                      "
                                    />

                                    <span
                                      className="
                                        text-[11px]
                                        font-semibold
                                        uppercase
                                        tracking-[0.28em]
                                        text-primaryColor
                                      "
                                    >
                                      WHAT WE OFFER
                                    </span>

                                    <div
                                      className="
                                        h-px
                                        flex-1
                                        bg-gradient-to-r
                                        from-primaryColor/40
                                        to-transparent
                                      "
                                    />
                                  </div>
                                </div>

                                {/* CATEGORIES */}

                                <div className="p-3">
                                  {serviceCategories.map(
                                    (category) => {
                                      const Icon =
                                        category.icon;

                                      return (
                                        <motion.div
                                          key={category.id}
                                          whileHover={{
                                            x: 3,
                                          }}
                                          onMouseEnter={() =>
                                            handleCategoryHover(
                                              category
                                            )
                                          }
                                          onClick={() => {
                                            if (
                                              category.comingSoon
                                            ) {
                                              return;
                                            }

                                            handleMainServiceNavigation(
                                              category.id
                                            );
                                          }}
                                          className={`
                                            group
                                            mb-2
                                            flex
                                            cursor-pointer
                                            items-center
                                            justify-between
                                            rounded-2xl
                                            px-4
                                            py-3
                                            transition-all
                                            duration-200
                                            border

                                            ${
                                              activeCategory ===
                                              category.id
                                                ? "border-primaryColor/20 bg-primaryColor/10"
                                                : "theme-border hover:bg-primaryColor/5"
                                            }

                                            ${
                                              category.comingSoon
                                                ? "cursor-default"
                                                : ""
                                            }
                                          `}
                                        >
                                          <div className="flex items-center gap-4">
                                            <div
                                              className={`
                                                flex
                                                h-11
                                                w-11
                                                items-center
                                                justify-center
                                                rounded-xl

                                                ${
                                                  activeCategory ===
                                                  category.id
                                                    ? "bg-primaryColor text-black"
                                                    : "theme-bg-tertiary text-primaryColor"
                                                }
                                              `}
                                            >
                                              <Icon size={19} />
                                            </div>

                                            <span
                                              className="
                                                font-medium
                                                theme-text-primary
                                              "
                                            >
                                              {category.title}
                                            </span>
                                          </div>

                                          {category.comingSoon ? (
                                            <span
                                              className="
                                                rounded-full
                                                border
                                                border-primaryColor/20
                                                bg-primaryColor/10
                                                px-3
                                                py-1
                                                text-[10px]
                                                font-semibold
                                                uppercase
                                                tracking-wider
                                                text-primaryColor
                                              "
                                            >
                                              Soon
                                            </span>
                                          ) : (
                                            <ChevronRight
                                              size={18}
                                              className={`
                                                transition-all
                                                duration-200

                                                ${
                                                  activeCategory ===
                                                  category.id
                                                    ? "translate-x-1 text-primaryColor"
                                                    : "theme-text-muted"
                                                }
                                              `}
                                            />
                                          )}
                                        </motion.div>
                                      );
                                    }
                                  )}
                                </div>
                              </div>

                              {/* DESKTOP FLYOUT */}

                              <AnimatePresence>
                                {activeCategory &&
                                  !serviceCategories.find(
                                    (category) =>
                                      category.id ===
                                        activeCategory &&
                                      category.comingSoon
                                  ) && (
                                    <motion.div
                                      initial={{
                                        opacity: 0,
                                        x: -10,
                                      }}
                                      animate={{
                                        opacity: 1,
                                        x: 0,
                                      }}
                                      exit={{
                                        opacity: 0,
                                        x: -10,
                                      }}
                                      transition={{
                                        duration: 0.18,
                                      }}
                                      className="
                                        ml-1
                                        mt-10
                                        w-[330px]
                                      "
                                    >
                                      <div
                                        className="
                                          overflow-hidden
                                          rounded-3xl
                                          border
                                          theme-border
                                          theme-card
                                          backdrop-blur-2xl
                                          shadow-[0_20px_60px_var(--shadow-color)]
                                        "
                                      >
                                        {/* FLYOUT HEADER */}

                                        <div
                                          className="
                                            border-b
                                            theme-border
                                            bg-gradient-to-r
                                            from-primaryColor/10
                                            via-primaryColor/5
                                            to-transparent
                                            px-5
                                            py-4
                                          "
                                        >
                                          <div className="flex items-center gap-3">
                                            <span
                                              className="
                                                h-2
                                                w-2
                                                rounded-full
                                                bg-primaryColor
                                              "
                                            />

                                            <span
                                              className="
                                                text-[11px]
                                                font-semibold
                                                uppercase
                                                tracking-[0.28em]
                                                text-primaryColor
                                              "
                                            >
                                              {
                                                servicesData[
                                                  activeCategory
                                                ]?.title
                                              }
                                            </span>

                                            <div
                                              className="
                                                h-px
                                                flex-1
                                                bg-gradient-to-r
                                                from-primaryColor/40
                                                to-transparent
                                              "
                                            />
                                          </div>
                                        </div>

                                        {/* SERVICES */}

                                        <div className="py-2">
                                          {activeServices.map(
                                            (
                                              service,
                                              index
                                            ) => {
                                              const Icon =
                                                service.icon;

                                              return (
                                                <motion.div
                                                  key={
                                                    service.slug ||
                                                    service.title
                                                  }
                                                  initial={{
                                                    opacity: 0,
                                                    x: 10,
                                                  }}
                                                  animate={{
                                                    opacity: 1,
                                                    x: 0,
                                                  }}
                                                  transition={{
                                                    delay:
                                                      index *
                                                      0.03,
                                                  }}
                                                  whileHover={{
                                                    x: 4,
                                                  }}
                                                  onClick={() =>
                                                    handleNavigate(
                                                      service.path
                                                    )
                                                  }
                                                  className="
                                                    group
                                                    mx-2
                                                    mb-1
                                                    cursor-pointer
                                                    rounded-2xl
                                                    px-3
                                                    py-2.5
                                                    transition-all
                                                    duration-200
                                                    hover:bg-primaryColor/5
                                                  "
                                                >
                                                  <div className="flex items-center justify-between">
                                                    <div className="flex items-center gap-3">
                                                      <div
                                                        className="
                                                          flex
                                                          h-9
                                                          w-9
                                                          items-center
                                                          justify-center
                                                          rounded-xl
                                                          theme-bg-tertiary
                                                          text-primaryColor
                                                          transition-all
                                                          duration-200
                                                          group-hover:bg-primaryColor
                                                          group-hover:text-black
                                                        "
                                                      >
                                                        <Icon size={17} />
                                                      </div>

                                                      <span
                                                        className="
                                                          text-[15px]
                                                          font-medium
                                                          theme-text-primary
                                                        "
                                                      >
                                                        {
                                                          service.title
                                                        }
                                                      </span>
                                                    </div>

                                                    <ArrowUpRight
                                                      size={16}
                                                      className="
                                                        theme-text-muted
                                                        transition-all
                                                        duration-200
                                                        group-hover:rotate-45
                                                        group-hover:text-primaryColor
                                                      "
                                                    />
                                                  </div>
                                                </motion.div>
                                              );
                                            }
                                          )}
                                        </div>
                                      </div>
                                    </motion.div>
                                  )}
                              </AnimatePresence>
                            </div>
                          </motion.div>
                        </>
                      )}
                    </AnimatePresence>
                  </>
                ) : (
                  <button
                    onClick={() =>
                      handleNavigate(item.path)
                    }
                    className={`
                      font-medium
                      transition-all
                      duration-300

                      ${
                        location.pathname === item.path
                          ? "text-primaryColor"
                          : "theme-text-secondary hover:text-primaryColor"
                      }
                    `}
                  >
                    {item.title}
                  </button>
                )}
              </div>
            ))}
          </div>

          {/* DESKTOP ACTIONS */}

          <div className="flex items-center gap-3">
            <ThemeToggleButton />

            <button
              onClick={() => setOpenModal(true)}
              className="
                group
                rounded-full
                bg-primaryColor
                px-5
                py-2
                font-semibold
                text-black
                transition-all
                duration-300
                hover:scale-105
              "
            >
              <span className="flex items-center gap-3">
                Get Started

                <span
                  className="
                    flex
                    h-9
                    w-9
                    items-center
                    justify-center
                    rounded-full
                    bg-black
                    text-white
                    transition-all
                    duration-300
                    group-hover:rotate-45
                    group-hover:bg-white
                    group-hover:text-black
                  "
                >
                  <ArrowUpRight size={18} />
                </span>
              </span>
            </button>
          </div>
        </div>

        {/* ================================================================= */}
        {/* MOBILE + TABLET NAVBAR                                           */}
        {/* ================================================================= */}

        <div
          className="
            webbocket-mobile-navbar
            xl:hidden
            flex
            min-h-[70px]
            sm:min-h-[74px]
            md:min-h-[78px]
            items-center
            justify-between
            rounded-2xl
            border
            theme-border
            theme-card
            px-3
            sm:px-4
            md:px-5
            shadow-[0_12px_40px_var(--shadow-color)]
            backdrop-blur-xl
            transition-all
            duration-300
          "
        >
          {/* MOBILE LOGO */}

          <div
            className="
              flex
              min-w-0
              flex-1
              h-full
              cursor-pointer
              items-center
            "
            onClick={() => handleNavigate("/")}
          >
            <img
              src={currentLogo}
              alt="WebBocket"
              className="
                h-[46px]
                w-[155px]
                origin-left
                scale-[1.45]
                object-contain
                object-left

                sm:h-[50px]
                sm:w-[170px]
                sm:scale-[1.3]

                md:h-[54px]
                md:w-[185px]
                md:scale-[1.3]
              "
            />
          </div>

          {/* MOBILE RIGHT ACTIONS */}

          <div className="flex items-center gap-1.5 sm:gap-2">

            {/* THEME BUTTON - ONLY HERE */}

            <ThemeToggleButton />

            {/* MENU BUTTON */}

            <button
              onClick={() => setMobileOpen(true)}
              className="
                flex
                h-10
                w-10
                shrink-0
                items-center
                justify-center
                rounded-xl
                border
                theme-border
                theme-bg-tertiary
                theme-text-primary
                transition-all
                duration-200
                hover:border-primaryColor/30
                hover:bg-primaryColor/10
                hover:text-primaryColor

                sm:h-11
                sm:w-11
              "
              aria-label="Open navigation menu"
            >
              <GiHamburgerMenu
                size={19}
                className="sm:hidden"
              />

              <GiHamburgerMenu
                size={21}
                className="hidden sm:block"
              />
            </button>
          </div>
        </div>

        {/* ================================================================= */}
        {/* MOBILE DRAWER                                                     */}
        {/* ================================================================= */}

        <AnimatePresence>
          {mobileOpen && (
            <>
              {/* BACKDROP */}

              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={closeMobileDrawer}
                className="
                  fixed
                  inset-0
                  z-40
                  backdrop-blur-md
                "
                style={{
                  background: isDark
                    ? "rgba(0,0,0,.70)"
                    : "rgba(0,0,0,.30)",
                }}
              />

              {/* DRAWER */}

              <motion.div
                ref={mobileDrawerRef}
                initial={{ x: "100%" }}
                animate={{ x: 0 }}
                exit={{ x: "100%" }}
                transition={{
                  type: "spring",
                  stiffness: 280,
                  damping: 28,
                }}
                className="
                  fixed
                  right-0
                  top-0
                  z-50
                  flex
                  h-full
                  w-[90%]
                  max-w-[430px]
                  flex-col
                  overflow-hidden
                  border-l
                  theme-border
                  theme-bg-primary
                  theme-text-primary
                  shadow-[-20px_0_70px_var(--shadow-color)]
                  transition-colors
                  duration-300
                "
              >
                {/* ========================================================= */}
                {/* DRAWER HEADER                                             */}
                {/* ========================================================= */}

                <div
                  className="
                    flex
                    min-h-[78px]
                    shrink-0
                    items-center
                    justify-between
                    border-b
                    theme-border
                    theme-bg-secondary
                    px-3

                    sm:min-h-[84px]
                    sm:px-5
                  "
                >
                  {/* LOGO */}

                  <div
                    className="
                      flex
                      h-full
                      min-w-0
                      flex-1
                      cursor-pointer
                      items-center
                    "
                    onClick={() =>
                      handleNavigate("/")
                    }
                  >
                    <img
                      src={currentLogo}
                      alt="WebBocket"
                      className="
                        h-[48px]
                        w-[165px]
                        origin-left
                        scale-[2.1]
                        object-contain
                        object-left

                        sm:h-[54px]
                        sm:w-[180px]
                        sm:scale-[1.35]
                      "
                    />
                  </div>

                  {/* ONLY CLOSE BUTTON HERE */}

                  <button
                    onClick={closeMobileDrawer}
                    className="
                      flex
                      h-10
                      w-10
                      shrink-0
                      items-center
                      justify-center
                      rounded-xl
                      border
                      theme-border
                      theme-bg-tertiary
                      theme-text-secondary
                      transition-all
                      duration-200
                      hover:border-primaryColor/30
                      hover:bg-primaryColor/10
                      hover:text-primaryColor

                      sm:h-11
                      sm:w-11
                    "
                    aria-label="Close menu"
                  >
                    <IoMdClose size={22} />
                  </button>
                </div>

                {/* ========================================================= */}
                {/* MENU CONTENT                                              */}
                {/* ========================================================= */}

                <div
                  className="
                    flex-1
                    overflow-y-auto
                    px-3
                    py-4

                    sm:px-5
                    sm:py-6
                  "
                >
                  <div className="space-y-2">
                    {menuItems.map((item) => (
                      <div key={item.title}>
                        {item.megaMenu ? (
                          /* SERVICES */

                          <div
                            className="
                              overflow-hidden
                              rounded-2xl
                              border
                              theme-border
                              theme-card
                            "
                          >
                            {/* SERVICES BUTTON */}

                            <button
                              onClick={
                                toggleMobileServices
                              }
                              className={`
                                flex
                                w-full
                                items-center
                                justify-between
                                px-4
                                py-4

                                sm:px-5
                                sm:py-4

                                transition-all
                                duration-300

                                ${
                                  mobileServicesOpen
                                    ? "bg-primaryColor/10"
                                    : "hover:bg-primaryColor/5"
                                }
                              `}
                            >
                              <span
                                className={`
                                  font-medium

                                  ${
                                    mobileServicesOpen
                                      ? "text-primaryColor"
                                      : "theme-text-primary"
                                  }
                                `}
                              >
                                Services
                              </span>

                              <motion.div
                                animate={{
                                  rotate:
                                    mobileServicesOpen
                                      ? 180
                                      : 0,
                                }}
                                transition={{
                                  duration: 0.2,
                                }}
                                className="theme-text-muted"
                              >
                                <IoIosArrowDown
                                  size={19}
                                />
                              </motion.div>
                            </button>

                            {/* SERVICE CATEGORIES */}

                            <AnimatePresence
                              initial={false}
                            >
                              {mobileServicesOpen && (
                                <motion.div
                                  initial={{
                                    height: 0,
                                    opacity: 0,
                                  }}
                                  animate={{
                                    height: "auto",
                                    opacity: 1,
                                  }}
                                  exit={{
                                    height: 0,
                                    opacity: 0,
                                  }}
                                  transition={{
                                    duration: 0.25,
                                    ease: "easeInOut",
                                  }}
                                  className="
                                    overflow-hidden
                                    border-t
                                    theme-border
                                  "
                                >
                                  <div
                                    className="
                                      space-y-2
                                      p-2.5

                                      sm:p-3
                                    "
                                  >
                                    {serviceCategories.map(
                                      (category) => {
                                        const Icon =
                                          category.icon;

                                        const isExpanded =
                                          mobileExpandedCategory ===
                                          category.id;

                                        return (
                                          <div
                                            key={
                                              category.id
                                            }
                                            className={`
                                              overflow-hidden
                                              rounded-xl
                                              border
                                              transition-all
                                              duration-300

                                              ${
                                                isExpanded
                                                  ? "border-primaryColor/20 bg-primaryColor/[0.035]"
                                                  : "theme-border theme-card"
                                              }
                                            `}
                                          >
                                            {/* CATEGORY ROW */}

                                            <div
                                              className={`
                                                flex
                                                min-h-[54px]
                                                items-center
                                                justify-between
                                                transition-all
                                                duration-300

                                                ${
                                                  isExpanded
                                                    ? "bg-primaryColor/10"
                                                    : "hover:bg-primaryColor/5"
                                                }
                                              `}
                                            >
                                              {/* CATEGORY NAME */}

                                              <button
                                                disabled={
                                                  category.comingSoon
                                                }
                                                onClick={() => {
                                                  if (
                                                    category.comingSoon
                                                  ) {
                                                    return;
                                                  }

                                                  handleMainServiceNavigation(
                                                    category.id
                                                  );
                                                }}
                                                className={`
                                                  flex
                                                  min-w-0
                                                  flex-1
                                                  items-center
                                                  gap-3
                                                  px-3
                                                  py-3
                                                  text-left

                                                  sm:px-4

                                                  ${
                                                    category.comingSoon
                                                      ? "cursor-default"
                                                      : "cursor-pointer"
                                                  }
                                                `}
                                              >
                                                <div
                                                  className={`
                                                    flex
                                                    h-8
                                                    w-8
                                                    shrink-0
                                                    items-center
                                                    justify-center
                                                    rounded-lg
                                                    transition-all
                                                    duration-300

                                                    ${
                                                      isExpanded
                                                        ? "bg-primaryColor text-black"
                                                        : "theme-bg-tertiary text-primaryColor"
                                                    }
                                                  `}
                                                >
                                                  <Icon size={16} />
                                                </div>

                                                <span
                                                  className={`
                                                    truncate
                                                    text-left
                                                    text-sm
                                                    font-medium

                                                    sm:text-[15px]

                                                    ${
                                                      isExpanded
                                                        ? "text-primaryColor"
                                                        : "theme-text-primary"
                                                    }
                                                  `}
                                                >
                                                  {
                                                    category.title
                                                  }
                                                </span>
                                              </button>

                                              {/* RIGHT */}

                                              {category.comingSoon ? (
                                                <span
                                                  className="
                                                    mr-3
                                                    shrink-0
                                                    rounded-full
                                                    border
                                                    border-primaryColor/20
                                                    bg-primaryColor/10
                                                    px-2.5
                                                    py-1
                                                    text-[9px]
                                                    font-semibold
                                                    uppercase
                                                    tracking-wider
                                                    text-primaryColor

                                                    sm:mr-4
                                                  "
                                                >
                                                  Soon
                                                </span>
                                              ) : (
                                                <button
                                                  type="button"
                                                  onClick={(
                                                    event
                                                  ) => {
                                                    event.stopPropagation();

                                                    toggleMobileCategory(
                                                      category.id
                                                    );
                                                  }}
                                                  className={`
                                                    mr-2
                                                    flex
                                                    h-8
                                                    w-8
                                                    shrink-0
                                                    items-center
                                                    justify-center
                                                    rounded-lg
                                                    transition-all
                                                    duration-200

                                                    sm:mr-3

                                                    ${
                                                      isExpanded
                                                        ? "bg-primaryColor/10 text-primaryColor"
                                                        : "theme-text-muted hover:bg-primaryColor/5 hover:text-primaryColor"
                                                    }
                                                  `}
                                                  aria-label={
                                                    isExpanded
                                                      ? `Collapse ${category.title}`
                                                      : `Expand ${category.title}`
                                                  }
                                                >
                                                  <motion.div
                                                    animate={{
                                                      rotate:
                                                        isExpanded
                                                          ? 90
                                                          : 0,
                                                    }}
                                                    transition={{
                                                      duration: 0.2,
                                                    }}
                                                  >
                                                    <ChevronRight
                                                      size={17}
                                                    />
                                                  </motion.div>
                                                </button>
                                              )}
                                            </div>

                                            {/* SUBSERVICES */}

                                            <AnimatePresence
                                              initial={false}
                                            >
                                              {isExpanded &&
                                                !category.comingSoon && (
                                                  <motion.div
                                                    initial={{
                                                      height: 0,
                                                      opacity: 0,
                                                    }}
                                                    animate={{
                                                      height:
                                                        "auto",
                                                      opacity: 1,
                                                    }}
                                                    exit={{
                                                      height: 0,
                                                      opacity: 0,
                                                    }}
                                                    transition={{
                                                      duration:
                                                        0.25,
                                                      ease:
                                                        "easeInOut",
                                                    }}
                                                    className="
                                                      overflow-hidden
                                                      border-t
                                                      theme-border
                                                    "
                                                  >
                                                    <div className="py-1.5">
                                                      {servicesData[
                                                        category.id
                                                      ]?.services?.map(
                                                        (
                                                          service,
                                                          index
                                                        ) => {
                                                          const ServiceIcon =
                                                            service.icon;

                                                          return (
                                                            <motion.button
                                                              key={
                                                                service.slug ||
                                                                service.title
                                                              }
                                                              initial={{
                                                                opacity: 0,
                                                                x: -8,
                                                              }}
                                                              animate={{
                                                                opacity: 1,
                                                                x: 0,
                                                              }}
                                                              transition={{
                                                                delay:
                                                                  index *
                                                                  0.035,
                                                                duration:
                                                                  0.2,
                                                              }}
                                                              onClick={() =>
                                                                handleNavigate(
                                                                  service.path
                                                                )
                                                              }
                                                              className="
                                                                group
                                                                flex
                                                                w-full
                                                                items-center
                                                                justify-between
                                                                px-4
                                                                py-2.5
                                                                text-left
                                                                transition-all
                                                                duration-200
                                                                hover:bg-primaryColor/5

                                                                sm:px-5
                                                              "
                                                            >
                                                              <div className="flex min-w-0 items-center gap-3">
                                                                <div
                                                                  className="
                                                                    flex
                                                                    h-7
                                                                    w-7
                                                                    shrink-0
                                                                    items-center
                                                                    justify-center
                                                                    rounded-lg
                                                                    theme-bg-tertiary
                                                                    text-primaryColor
                                                                    transition-all
                                                                    duration-200
                                                                    group-hover:bg-primaryColor
                                                                    group-hover:text-black
                                                                  "
                                                                >
                                                                  <ServiceIcon
                                                                    size={
                                                                      13
                                                                    }
                                                                  />
                                                                </div>

                                                                <span
                                                                  className="
                                                                    truncate
                                                                    text-[12px]
                                                                    font-medium
                                                                    theme-text-secondary
                                                                    transition-colors
                                                                    duration-200
                                                                    group-hover:text-primaryColor

                                                                    sm:text-[13px]
                                                                  "
                                                                >
                                                                  {
                                                                    service.title
                                                                  }
                                                                </span>
                                                              </div>

                                                              <ArrowUpRight
                                                                size={13}
                                                                className="
                                                                  ml-2
                                                                  shrink-0
                                                                  theme-text-muted
                                                                  transition-all
                                                                  duration-200
                                                                  group-hover:rotate-45
                                                                  group-hover:text-primaryColor
                                                                "
                                                              />
                                                            </motion.button>
                                                          );
                                                        }
                                                      )}
                                                    </div>
                                                  </motion.div>
                                                )}
                                            </AnimatePresence>
                                          </div>
                                        );
                                      }
                                    )}
                                  </div>
                                </motion.div>
                              )}
                            </AnimatePresence>
                          </div>
                        ) : (
                          /* NORMAL MENU ITEM */

                          <button
                            onClick={() =>
                              handleNavigate(item.path)
                            }
                            className={`
                              w-full
                              rounded-2xl
                              px-4
                              py-4
                              text-left
                              transition-all
                              duration-300

                              sm:px-5
                              sm:py-4

                              ${
                                location.pathname ===
                                item.path
                                  ? "bg-primaryColor font-semibold text-black"
                                  : "theme-text-primary hover:bg-primaryColor/5 hover:text-primaryColor"
                              }
                            `}
                          >
                            {item.title}
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* ========================================================= */}
                {/* DRAWER FOOTER                                             */}
                {/* ========================================================= */}

                <div
                  className="
                    shrink-0
                    border-t
                    theme-border
                    theme-bg-secondary
                    p-4

                    sm:p-5
                  "
                >
                  {/* GET STARTED */}

                  <button
                    onClick={() => {
                      closeMobileDrawer();
                      setOpenModal(true);
                    }}
                    className="
                      group
                      flex
                      w-full
                      items-center
                      justify-center
                      gap-3
                      rounded-2xl
                      bg-primaryColor
                      py-3.5
                      font-semibold
                      text-black
                      transition-all
                      duration-300
                      hover:scale-[1.01]

                      sm:py-4
                    "
                  >
                    <span>Get Started</span>

                    <span
                      className="
                        flex
                        h-9
                        w-9
                        items-center
                        justify-center
                        rounded-full
                        bg-black
                        text-white
                        transition-all
                        duration-300
                        group-hover:rotate-45
                        group-hover:bg-white
                        group-hover:text-black
                      "
                    >
                      <ArrowUpRight size={17} />
                    </span>
                  </button>

                  {/* COPYRIGHT */}

                  <p
                    className="
                      mt-3
                      text-center
                      text-[10px]
                      theme-text-muted

                      sm:text-xs
                    "
                  >
                    © {new Date().getFullYear()} WebBocket.
                    All Rights Reserved.
                  </p>
                </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>

        {/* ================================================================= */}
        {/* CONTACT MODAL                                                     */}
        {/* ================================================================= */}

        <ContactModal
          isOpen={openModal}
          onClose={() => setOpenModal(false)}
        />
      </div>
    </>
  );
};

export default BottomNavbar;